import Modal from "@/components/common/Modal";
import { useRequests } from "@/components/pages/requests/hooks/useRequests";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import React, { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useUpdateTask } from "@/components/pages/requests/hooks/tasks.hooks";
import { Task } from "@/types/tasks";
import { Request, RequestStatus } from "@/types/request";
import { useTranslation } from "react-i18next";

interface AssignTaskToStaffModalProps {
  staffId: string;
  trigger: React.ReactNode;
}

export const AssignTaskToStaffModal: React.FC<AssignTaskToStaffModalProps> = ({
  staffId,
  trigger,
}) => {
  const { t } = useTranslation();
  const hotelId = useCurrentHotelId();
  const { capabilities } = useAccessCapabilities();
  const [open, setOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<
    string | undefined
  >(undefined);

  const startDate = open ? new Date().toISOString() : undefined;

  const { data: requestsData, isLoading } = useRequests(
    {
      hotelId: hotelId ?? "",
      unassigned: true,
      status: [RequestStatus.NEW_TASK, RequestStatus.IN_PROGRESS],
      dueDateTimeStart: startDate,
    },
    open && !!hotelId && capabilities.canUseRequestsSystem,
  );

  const { mutate: updateTask, isPending: isUpdating } = useUpdateTask();

  const handleAssign = () => {
    if (!selectedRequestId) return;

    const request = requestsData?.data.find((r) => r.id === selectedRequestId);
    const task = request?.tasks?.[0] as Task | undefined;

    if (task?.id) {
      updateTask(
        {
          id: task.id,
          data: { staffId: staffId },
        },
        {
          onSuccess: () => {
            setOpen(false);
            setSelectedRequestId(undefined);
          },
        },
      );
    }
  };

  const requests = requestsData?.data || [];
  const isRequestAccessDisabled = !capabilities.canUseRequestsSystem;

  return (
    <Modal
      title={t("requests.assignTask.title")}
      trigger={trigger}
      open={open}
      onOpenChange={setOpen}
      className="max-w-md"
    >
      <div className="flex flex-col gap-4 py-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">
            {t("requests.assignTask.selectTask")}
          </label>
          <Select
            value={selectedRequestId}
            onValueChange={setSelectedRequestId}
            disabled={isLoading || isUpdating || isRequestAccessDisabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={t("requests.assignTask.selectTaskPlaceholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {requests.length === 0 ? (
                <div className="p-2 text-sm text-gray-500 text-center">
                  {t("requests.assignTask.noTasks")}
                </div>
              ) : (
                requests.map((request: Request) => (
                  <SelectItem key={request.id} value={request.id!}>
                    {request.service?.name} -{" "}
                    {request.guest?.profile?.firstName}{" "}
                    {request.guest?.profile?.lastName} (
                    {request.booking?.room?.number ||
                      t("requests.assignTask.noRoom")}
                    )
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isUpdating}
          >
            {t("requests.form.cancel")}
          </Button>
          <Button
            onClick={handleAssign}
            disabled={
              !selectedRequestId || isUpdating || isRequestAccessDisabled
            }
          >
            {isUpdating
              ? t("requests.assignTask.assigning")
              : t("requests.assignTask.assign")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
